import React, { useEffect, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';

import { Spinner } from '@components/common';
import Skeleton from '@components/Skeleton';

import { useDownloadFile } from '@hooks/common/useDownloadFile';
import { useQueryUser } from '@hooks/useQueryUser';

import { store, useAppDispatch } from '@store/store';

import {
	setAvatar,
	setBranding,
	setOrganizationCategories,
	setOrganizationTimezone,
	setUser,
	setUserId,
} from '@slices/auth';
import { setBrowser, setIsProductionIdams, setSnackbar } from '@slices/common';

import WebsocketService from '@api/websocket';

import { getBrowser } from '@utils/browserUtils/browserUtils';

import { TOrganisationType } from '@interfaces/organisation/organisations';
import { IUserKeycloak } from '@interfaces/user/user';

import { ESnackbarTypes } from '@enums/common';

import { responseMessages } from '@constants/responseMessages';

import styles from './Root.module.scss';

interface IRootProps {
	currentUser?: IUserKeycloak | null;
}

const Root = ({
	currentUser,
}: IRootProps) => {
	const dispatch = useAppDispatch();
	const [isOnline, setOnline] = useState(true);
	const { keycloak, initialized } = useKeycloak();

	const navigate = useNavigate();

	const { user } = useQueryUser();

	useEffect(() => {
		if (currentUser) {
			dispatch(setUser(currentUser));
		}
	}, [currentUser, dispatch]);

	useEffect(() => {
		const browser = getBrowser();
		const isMobileDevice = !isDesktop;

		dispatch(setBrowser({ browser, isMobileDevice }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setOnline(navigator.onLine);
		dispatch(setIsProductionIdams(window.location.host === 'idams.awtg.co.uk'));
	}, [dispatch]);

	window.addEventListener('online', () => {
		setOnline(true);
	});

	window.addEventListener('offline', () => {
		setOnline(false);
	});

	useEffect(() => {
		if (isOnline) return;

		store.dispatch(setSnackbar({
			type: ESnackbarTypes.error, message: responseMessages.error,
		}));

		return () => {
			void WebsocketService.disconnectWebsocket();
		};
	}, [isOnline]);

	useEffect(() => {
		if (initialized && keycloak && !keycloak.authenticated) {
			keycloak.login();
		}
	}, [initialized, keycloak, navigate]);

	const organizationCategories = user?.organization?.categories?.map(({ organisationType }: {
		organisationType: TOrganisationType;
	}) => organisationType?.name);

	useEffect(() => {
		if (user) {
			dispatch(setOrganizationCategories(organizationCategories));
			dispatch(setOrganizationTimezone(user.organization?.timezone));
			dispatch(setUserId(user.id));
			dispatch(setAvatar(user.avatar));
			dispatch(setBranding(user.organization?.branding));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	const brandingName = user?.organization?.branding?.logoCaption;

	const { downloadUrl } = useDownloadFile({ id: user?.organization?.branding?.logo?.id });

	useEffect(() => {
		if (brandingName) document.title = brandingName;

		const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

		if (favicon && downloadUrl) favicon.href = downloadUrl;
	}, [user, downloadUrl, brandingName]);

	return keycloak && keycloak.authenticated ? <Skeleton /> : (<div className={styles.spinner}>
		<Spinner
			size={100}
			spinnerColorId={user?.organization?.branding?.spinnerColorId}
			spinnerId={user?.organization?.branding?.spinnerId}
		/>
	</div>);
};

export default Root;
